import { useEffect, useRef, useState } from 'react';
import { createSseConnection, type SseKind } from '../lib/sse';

export type SseStatus = 'idle' | 'connecting' | 'open' | 'error';

export interface UseSseQueryArgs {
  kind: SseKind;
  cluster?: string;
  enabled?: boolean;
}

export interface UseSseQueryResult<T> {
  data: T | undefined;
  status: SseStatus;
  lastEventAt: number | null;
}

export function useSseQuery<T>(args: UseSseQueryArgs): UseSseQueryResult<T> {
  const { kind, cluster, enabled = true } = args;
  const [data, setData] = useState<T | undefined>(undefined);
  const [status, setStatus] = useState<SseStatus>('idle');
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const onMessageRef = useRef<((d: unknown) => void) | null>(null);

  onMessageRef.current = (parsed: unknown): void => {
    setData(parsed as T);
    setLastEventAt(Date.now());
  };

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }
    setStatus('connecting');
    const conn = createSseConnection({
      kind,
      cluster,
      onMessage: d => onMessageRef.current?.(d),
      onOpen: () => setStatus('open'),
      onError: () => setStatus('error'),
    });
    return () => conn.close();
  }, [kind, cluster, enabled]);

  return { data, status, lastEventAt };
}
